import { FieldTemplate } from 'api/models';
import { FieldAttributesOptions, FieldType } from 'api/models/FieldTemplate';
import i18next, { TFunction } from 'i18next';
import * as yup from 'yup';

export const DEFAULT_SELECT_OPTIONS = [{ label: '', value: '' }];
export function getDefaultFormFieldValues(field: FieldTemplate) {
  const commonValues = {
    label: field.fieldAttributes.label || '',
    defaultValue: field.fieldAttributes.defaultValue || '',
    unit: field.fieldAttributes.unit || '',
    description: field.fieldAttributes.description || '',
    isRequired: field.fieldAttributes.isRequired || false,
    alias: field.alias || '',
    isRequiredConditions: field.isRequired,
    isVisibleConditions: field.isVisible,
    isRepeatable: field.fieldAttributes.isRepeatable ?? false,
    mappings: field.mappings ?? {},
  };

  const numberFieldValues = {
    minValue: field.fieldAttributes.minValue || '',
    maxValue: field.fieldAttributes.maxValue || '',
    minNormalValue: field.fieldAttributes.minNormalValue || '',
    maxNormalValue: field.fieldAttributes.maxNormalValue || '',
  };

  const stringFieldValues = {
    minLength: field.fieldAttributes.minLength || '',
    maxLength: field.fieldAttributes.maxLength || '',
    visibleLineCount: field.fieldAttributes.visibleLineCount || '',
  };

  const selectFieldValues = {
    options: field.fieldAttributes.options || DEFAULT_SELECT_OPTIONS,
    isMultiSelect: field.fieldAttributes.isMultiSelect || false,
    defaultValue: field.fieldAttributes.defaultValue
      ? field.fieldAttributes.defaultValue
      : field.fieldAttributes.isMultiSelect
      ? []
      : '',
    optionsSourceCodeLibraryId:
      field.fieldAttributes.optionsSourceCodeLibraryId || '',
    optionsSourceType: field.fieldAttributes.optionsSourceType || '',
    optionsSourceFieldTemplateId:
      field.fieldAttributes.optionsSourceFieldTemplateId || '',
    optionsSourceFieldAlias:
      field.fieldAttributes.optionsSourceFieldAlias || null,
    component: field.fieldAttributes.component || false,
  };

  const dateFieldValues = {
    dateType: field.fieldAttributes.dateType || 'YYYY.MM.DD',
    defaultValueType: field.fieldAttributes.defaultValueType || '',
    cantBeEarlierThanBirthDate:
      field.fieldAttributes.cantBeEarlierThanBirthDate ?? false,
    cantBeLaterThanVisitDate:
      field.fieldAttributes.cantBeLaterThanVisitDate ?? false,
  };

  const calculatedFieldValues = {
    code: field.fieldAttributes.code || '',
  };

  if (field.fieldType === FieldType.Number) {
    return { ...commonValues, ...numberFieldValues };
  }

  if (field.fieldType === FieldType.Text) {
    return { ...commonValues, ...stringFieldValues };
  }

  if (field.fieldType === FieldType.Select) {
    return { ...commonValues, ...selectFieldValues };
  }

  if (field.fieldType === FieldType.Date) {
    return { ...commonValues, ...dateFieldValues };
  }

  if (field.fieldType === FieldType.Calculated) {
    return { ...commonValues, ...calculatedFieldValues };
  }

  return commonValues;
}

export function getFormFieldValidationSchema(
  field: FieldTemplate,
  t: TFunction,
) {
  const defaultValidators = {
    label: yup.string().required(t('general.errors.mandatoryField') ?? ''),
    isVisibleConditions: yup
      .mixed()
      .test('conditional-validation', '', function (value) {
        // If value is undefined, validation passes
        if (value === undefined) {
          return true;
        }

        // Define the validation schema for when value exists
        const conditionGroupSchema = yup.object().shape({
          type: yup.string().required().equals(['conditionGroup']),
          operation: yup.string().required().oneOf(['and', 'or']),
          conditions: yup
            .array()
            .of(
              yup.lazy(condition => {
                // Define the reference schema
                const referenceSchema = yup
                  .object()
                  .shape({
                    type: yup
                      .string()
                      .required(t('general.errors.mandatoryField'))
                      .oneOf(['patientFieldReference', 'formFieldReference']),
                    valueType: yup.string(),
                    alias: yup.string(),
                    attribute: yup.string(),
                  })
                  .test(
                    'has-alias-or-attribute',
                    t('general.errors.eitherAliasOrAttribute'),
                    ref => {
                      if (!ref) return false;
                      return Boolean(
                        ref.alias?.trim() || ref.attribute?.trim(),
                      );
                    },
                  );

                // Define the condition schema
                const conditionSchema = yup.object().shape({
                  type: yup
                    .string()
                    .required(t('general.errors.mandatoryField'))
                    .equals(['condition']),
                  operation: yup
                    .string()
                    .required(t('general.errors.mandatoryField')),
                  value: yup
                    .mixed()
                    // Removed .required() to make value optional
                    .test('not-empty', t('general.errors.emptyValue'), val => {
                      // If value is undefined or null, validation passes
                      if (val === undefined || val === null) {
                        return true;
                      }
                      if (Array.isArray(val)) {
                        return true;
                      }
                      return val !== '';
                    }),
                  reference: referenceSchema.required(
                    t('general.errors.mandatoryField'),
                  ),
                });

                // Return appropriate schema based on type
                if (condition?.type === 'conditionGroup') {
                  return yup.object().shape({
                    type: yup
                      .string()
                      .required(t('general.errors.mandatoryField'))
                      .equals(['conditionGroup']),
                    operation: yup
                      .string()
                      .required(t('general.errors.mandatoryField'))
                      .oneOf(['and', 'or']),
                    conditions: yup
                      .array()
                      .of(yup.lazy(() => conditionSchema))
                      .required(t('general.errors.mandatoryField'))
                      .min(1, t('general.errors.minimumOneCondition')),
                  });
                }
                return conditionSchema;
              }),
            )
            .required(t('general.errors.mandatoryField'))
            .min(1, t('general.errors.minimumOneCondition')),
        });

        try {
          // Synchronously validate the value
          conditionGroupSchema.validateSync(value);
          return true;
        } catch (err) {
          // @ts-expect-error Yup provides error as unknown
          return this.createError({ message: err?.message });
        }
      }),
    mappings: yup.object().shape({
      standardCodeLibrary: yup.object().shape({
        fieldIdentifier: yup.string().notRequired(),
        valueMapping: yup.object().notRequired(),
      }),
      externalCommunication: yup.object().shape({
        hl7Identifier: yup
          .string()
          .when('hl7OverrideEnabled', (hl7ImportRule, schema) => {
            const rule = hl7ImportRule?.[0];
            if (rule) {
              return schema.required(t('general.errors.mandatoryField') ?? '');
            }
            return schema;
          }),
        hl7ImportRule: yup.string().notRequired(),
        hl7OverrideEnabled: yup.boolean().notRequired(),
      }),
    }),
  };

  // TODO: Fix validations
  const selectValidators = {
    options: yup.array().of(
      yup.object().shape({
        label: yup
          .string()
          .when('optionsSourceCodeLibraryId', e => {
            if (field.fieldAttributes.optionsSourceCodeLibraryId) {
              return yup.string().notRequired();
            }
            return yup
              .string()
              .required(t('general.errors.mandatoryField') ?? '');
          })
          .test(
            'unique-label',
            t('general.errors.hasToBeUniqueLabel') ?? '',
            function (value, context) {
              const options: { label: string; value: string }[] =
                context?.from?.[1]?.value?.options;
              return (
                options?.filter(option => option.label === value).length === 1
              );
            },
          ),

        value: yup
          .string()
          .when('optionsSourceCodeLibraryId', (e, a) => {
            if (field.fieldAttributes.optionsSourceCodeLibraryId) {
              return yup.string().notRequired();
            }
            return yup
              .string()
              .required(t('general.errors.mandatoryField') ?? '');
          })
          .test(
            'unique-value',
            t('general.errors.hasToBeUniqueValue') ?? '',
            function (value, context) {
              const options: { label: string; value: string }[] =
                context?.from?.[1]?.value?.options;

              return (
                options?.filter(option => option.value === value).length === 1
              );
            },
          ),
      }),
    ),
    optionsSourceCodeLibraryId: yup.string().notRequired(),
  };

  if (field.fieldType === FieldType.Select) {
    return yup.object().shape({ ...defaultValidators, ...selectValidators });
  }

  return yup.object().shape(defaultValidators);
}

export function getUsedByFieldsLabel({
  sourceId,
  fields,
  fieldId,
}: {
  sourceId: string;
  fields?: FieldTemplate[];
  fieldId?: string;
}) {
  const filteredFields = fields?.filter(
    field => field.fieldTemplateId !== fieldId,
  );
  const fieldsLength = filteredFields?.length ?? 0;

  if (isCodeLibrary({ sourceId })) {
    return '';
  }

  if (fieldsLength > 0) {
    return i18next.t('forms.otherFieldsUseIt', {
      number: fieldsLength,
    });
  }

  return i18next.t('forms.onlyThisFieldUsesIt');
}

export function isCodeLibrary({ sourceId }: { sourceId: string }) {
  // if we have a mapping for it in the translations it means it's a code library
  return i18next.exists(`forms.codeLibraries.${sourceId}`);
}

interface ErrorObject {
  options: Array<{
    label?: string;
    value?: string;
  } | null>;
}
// TODO: get rid of this once its not used no more
export function validateOptions(
  objects: FieldAttributesOptions[],
): ErrorObject {
  const errors: ErrorObject = { options: [] };
  const labels = new Set<string>();
  const values = new Set<string>();

  objects.forEach((obj, index) => {
    const { label, value } = obj;

    if (!label) {
      errors.options.push({
        label: i18next.t('general.errors.mandatoryField'),
      });
    } else if (!value) {
      errors.options.push({
        value: i18next.t('general.errors.mandatoryField'),
      });
    } else {
      errors.options.push(null);
    }

    if (labels.has(label)) {
      errors.options[index] = {
        label: i18next.t('general.errors.hasToBeUniqueValue'),
      };
    } else {
      labels.add(label);
    }

    if (values.has(value)) {
      errors.options[index] = {
        value: i18next.t('general.errors.hasToBeUniqueValue'),
      };
    } else {
      values.add(value);
    }
  });

  return errors;
}

export function getIsDisabledSaveButton({
  isSubmitting,
  label,
  alias,
  disabled,
  isValid,
}: {
  isSubmitting?: boolean;
  label?: string;
  alias?: string;
  disabled?: boolean;
  isValid?: boolean;
}) {
  if (
    isSubmitting || !label || alias ? !alias?.match(/^[a-zA-Z0-9_]+$/) : false
  ) {
    return true;
  }

  if (disabled) {
    return true;
  }

  if (isValid === false) {
    return true;
  }

  return false;
}
